import { useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Login } from './pages/Login';
import { AuthCallback } from './pages/AuthCallback';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { AdminUsers } from './pages/AdminUsers';
import { Notifications } from './pages/Notifications';
import { SchedulePage } from './pages/SchedulePage';
import { ProjectSchedulePage } from './pages/ProjectSchedulePage';
import { MySchedulePage } from './pages/MySchedulePage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { CategoriesPage } from './pages/admin/CategoriesPage';
import { IngestRunsPage } from './pages/admin/IngestRunsPage';
import { LoginLogPage } from './pages/admin/LoginLogPage';
import { AdminRoute } from './components/AdminRoute';
import { NotificationBell } from './components/NotificationBell';
import { MobileNavDrawer } from './components/mobile/MobileNavDrawer';
import { useCurrentUser } from './hooks/useCurrentUser';
import { useIsAdmin } from './hooks/useIsAdmin';
import { useIsMobile } from './hooks/useIsMobile';
import { apiClient } from './lib/api';
import { getClientConfig } from './lib/config';
import { logoutRedirect } from './lib/auth';

// The mobile header shows the current page name where the desktop shows the whole nav.
// Longest prefix wins, so /projects/:jobNumber picks up "Project".
const PAGE_TITLES: [prefix: string, title: string][] = [
  ['/schedule', 'Schedule'],
  ['/project-schedule', 'By Project'],
  ['/me', 'My Schedule'],
  ['/projects', 'Projects'],
  ['/as-of', 'As Of'],
  ['/notifications', 'Notifications'],
  ['/admin/users', 'Users'],
  ['/admin/categories', 'Categories'],
  ['/admin/ingest', 'Ingest'],
  ['/admin/login-log', 'Login Log'],
];

function pageTitleFor(pathname: string): string {
  const match = PAGE_TITLES.filter(([prefix]) => pathname.startsWith(prefix)).sort(
    (a, b) => b[0].length - a[0].length,
  )[0];
  return match?.[1] ?? 'ProjectCalendar';
}

export default function App() {
  const { data: user, isLoading } = useCurrentUser();
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [navOpen, setNavOpen] = useState(false);

  // The schedule grid wants the full viewport width — no centered container, no padding.
  // Every other page still gets the bounded, padded `<main>` so forms and tables don't
  // sprawl across an ultra-wide monitor. On a phone /me joins them: its mobile agenda is
  // a full-bleed list that scrolls under a sticky header, same as the schedule pages.
  const isFullWidthRoute =
    location.pathname.startsWith('/schedule') ||
    location.pathname.startsWith('/project-schedule') ||
    (isMobile && location.pathname.startsWith('/me'));
  const mainClass = isMobile
    ? `flex-1 w-full flex flex-col min-h-0${isFullWidthRoute ? '' : ' px-3 py-4'}`
    : isFullWidthRoute
      ? 'flex-1 w-full flex flex-col min-h-0 px-3'
      : 'flex-1 max-w-7xl mx-auto w-full px-4 py-6';

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive ? 'bg-brand-blue text-white' : 'text-slate-700 hover:bg-slate-200'
    }`;

  const adminMenuItemClass = ({ isActive }: { isActive: boolean }) =>
    `block px-3 py-1.5 text-sm transition-colors ${
      isActive
        ? 'bg-brand-blue/10 text-brand-blue font-medium'
        : 'text-slate-700 hover:bg-slate-100'
    }`;

  // Admin dropdown state — collapsed to a single "Admin ▾" button. Click toggles,
  // Esc or click-outside closes. Picking any item navigates and closes.
  const [adminOpen, setAdminOpen] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const isAdminRouteActive = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (!adminOpen) return;
    function onMouseDown(e: MouseEvent) {
      if (!adminMenuRef.current?.contains(e.target as Node)) setAdminOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setAdminOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [adminOpen]);

  // Navigating always closes the drawer. The NavLinks close it themselves; this catches
  // the redirect cases (auth gate, <Navigate>) that don't go through a click.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  async function signOut() {
    const cfg = await getClientConfig();
    if (cfg.authProvider === 'Okta' || cfg.authProvider === 'Entra') {
      // OIDC: clear the cached user and redirect to the IdP's end-session endpoint.
      await qc.invalidateQueries({ queryKey: ['me'] });
      try {
        await logoutRedirect();
        return;
      } catch {
        // Fall through to a local clear if the IdP redirect could not be started.
      }
    }
    try {
      await apiClient.post('/api/auth/logout', {});
    } catch {
      // ignore — clear local state regardless
    }
    sessionStorage.removeItem('access_token');
    await qc.invalidateQueries({ queryKey: ['me'] });
    navigate('/login');
  }

  const isCrew =
    user?.isAuthenticated &&
    !isAdmin &&
    user.roles.some((r) => r.toLowerCase() === 'user') &&
    !user.roles.some((r) => r.toLowerCase() === 'admin');

  // The login page and OIDC callback are always reachable, even while unauthenticated. Render
  // them standalone (they are full-screen pages, not part of the app shell).
  if (location.pathname === '/login') {
    return <Login />;
  }
  if (location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }
  // Invitees have no account yet, so this must render before the auth gate.
  if (location.pathname === '/accept-invite') {
    return <AcceptInvitePage />;
  }

  // Auth gate: until we know who the user is, hold the render. Once we know they are not
  // authenticated, send them to the dedicated /login page rather than the app shell.
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Loading...
      </div>
    );
  }

  if (!user?.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    // Full-width schedule routes lock the shell to the viewport so the grid scrolls
    // internally and the legend/footer stay pinned on screen. Other routes keep
    // min-h-screen so long forms and tables scroll the document naturally. On mobile the
    // lock is 100dvh, not 100vh — iOS Safari's collapsing toolbar overhangs `h-screen`
    // and clips the bottom of the list.
    <div
      className={`${
        isFullWidthRoute ? (isMobile ? 'h-[100dvh]' : 'h-screen') : 'min-h-screen'
      } flex flex-col`}
    >
      {isMobile ? (
        <header className="bg-brand-blue text-white shadow-sm pt-[env(safe-area-inset-top)]">
          <div className="flex items-center gap-1 px-1 py-1">
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              aria-label="Open navigation"
              aria-expanded={navOpen}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md active:bg-white/10"
            >
              <MenuIcon />
            </button>
            <h1 className="text-base font-semibold truncate">{pageTitleFor(location.pathname)}</h1>
            {/* The shared bell styles itself for the light desktop header; on the blue bar
                its trigger needs white ink and the unread badge needs a color that isn't
                the bar itself. `bg-white text-brand-blue` (9:1) replaces the default
                `bg-brand-blue text-white`, which would disappear against this header —
                brand-orange was tried here first but white text on it is only 3.33:1,
                below the 4.5:1 this 10px bold count needs. Scoped overrides beat forking
                the component. */}
            <div className="ml-auto [&>div>button]:text-white [&>div>button:hover]:bg-white/10 [&>div>button>span]:bg-white [&>div>button>span]:text-brand-blue">
              <NotificationBell />
            </div>
          </div>
        </header>
      ) : (
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-6">
          <div className="text-lg font-bold text-brand-blue">ProjectCalendar</div>
          <nav className="flex gap-1 items-start">
            <NavLink to="/schedule" className={navClass}>Schedule</NavLink>
            <NavLink to="/project-schedule" className={navClass}>By Project</NavLink>
            <NavLink to="/me" className={navClass}>My Schedule</NavLink>
            <NavLink to="/projects" className={navClass}>Projects</NavLink>
            {isAdmin && (
              <div ref={adminMenuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setAdminOpen((o) => !o)}
                  aria-expanded={adminOpen}
                  aria-haspopup="menu"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isAdminRouteActive ? 'bg-brand-blue text-white' : 'text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Admin <span aria-hidden="true">▾</span>
                </button>
                {adminOpen && (
                  <div
                    role="menu"
                    className="absolute top-full left-0 mt-1 bg-white rounded-md shadow-lg border border-slate-200 py-1 min-w-[180px] z-30"
                  >
                    <NavLink role="menuitem" to="/admin/categories" className={adminMenuItemClass} onClick={() => setAdminOpen(false)}>Categories</NavLink>
                    <NavLink role="menuitem" to="/admin/ingest" className={adminMenuItemClass} onClick={() => setAdminOpen(false)}>Ingest</NavLink>
                    <NavLink role="menuitem" to="/admin/login-log" className={adminMenuItemClass} onClick={() => setAdminOpen(false)}>Login Log</NavLink>
                    <NavLink role="menuitem" to="/admin/users" className={adminMenuItemClass} onClick={() => setAdminOpen(false)}>Users</NavLink>
                  </div>
                )}
              </div>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm text-slate-600">
            {isCrew && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-brand-charcoal/10 text-brand-charcoal font-medium">
                Crew
              </span>
            )}
            <NotificationBell />
            <span>{user.email}</span>
            <button onClick={signOut} className="text-brand-blue hover:underline">Sign out</button>
          </div>
        </div>
      </header>
      )}
      {isMobile && (
        <MobileNavDrawer
          open={navOpen}
          onClose={() => setNavOpen(false)}
          email={user.email ?? ''}
          isAdmin={isAdmin}
          isCrew={!!isCrew}
          signOut={signOut}
        />
      )}
      <main className={mainClass}>
        <Routes>
          <Route path="/" element={<Navigate to="/schedule" replace />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/project-schedule" element={<ProjectSchedulePage />} />
          <Route path="/me" element={<MySchedulePage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:jobNumber" element={<ProjectDetailPage />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="/admin/categories" element={<AdminRoute><CategoriesPage /></AdminRoute>} />
          <Route path="/admin/ingest" element={<AdminRoute><IngestRunsPage /></AdminRoute>} />
          <Route path="/admin/login-log" element={<AdminRoute><LoginLogPage /></AdminRoute>} />
        </Routes>
      </main>
      {/* A branding-only row isn't worth a phone's vertical space. */}
      <footer className="hidden md:block border-t border-slate-200 py-3 text-center text-xs text-slate-500">
        ProjectCalendar
      </footer>
    </div>
  );
}

function MenuIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}
